import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface VacationBalance {
  employee_id: number;
  hire_date: string;
  years_tenure: number;
  hours_earned: number;
  hours_used: number;
  hours_remaining: number;
  hours_prorated: number;
}

interface VacationRequest {
  request_id: number;
  employee_id: number;
  request_type: string;
  start_date: string;
  end_date: string;
  hours_requested: number;
  status: string;
  year: number;
  is_prorated: boolean;
  created_at: string;
}

export const VacationTime: React.FC = () => {
  const auth = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [balance, setBalance] = useState<VacationBalance | null>(null);
  const [requests, setRequests] = useState<VacationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showRequestOptions, setShowRequestOptions] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [customHours, setCustomHours] = useState('8');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMsg, setErrorModalMsg] = useState('');

  useEffect(() => {
    loadVacationData();
  }, []);

  const loadVacationData = async () => {
    if (!auth?.user?.employee_id) return;

    try {
      setLoading(true);
      setError(null);

      // Load balance
      const balanceResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/vacation/public/balance/${auth.user.employee_id}`,
        {
          headers: {
            'Authorization': `Bearer ${auth.token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const balanceData = await balanceResponse.json();
      if (balanceData.success) {
        setBalance(balanceData.data);
      } else {
        setError(balanceData.error || 'Failed to load vacation balance');
      }

      // Load requests
      const requestsResponse = await fetch(
        `${import.meta.env.VITE_API_URL}/api/vacation/requests/${auth.user.employee_id}`,
        {
          headers: {
            'Authorization': `Bearer ${auth.token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const requestsData = await requestsResponse.json();
      if (requestsData.success) {
        setRequests(requestsData.data || []);
      }
    } catch (err) {
      console.error('Failed to load vacation data:', err);
      setError('Failed to load vacation data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestVacation = async (requestType: 'all_available' | 'one_week' | 'weekly_until_depleted' | 'custom') => {
    if (!auth?.user?.employee_id) return;

    const showErr = (msg: string) => { setErrorModalMsg(msg); setShowErrorModal(true); };

    let hours = 0;
    if (requestType === 'custom') {
      hours = parseFloat(customHours);
      if (!hours || hours <= 0) { showErr('Enter a number of hours greater than 0'); return; }
      if (balance && hours > balance.hours_remaining) { showErr(`Only ${balance.hours_remaining} ${t('vacation_hours') || 'hours'} available`); return; }
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/vacation/request`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${auth.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            employee_id: auth.user.employee_id,
            request_type: requestType,
            ...(requestType === 'custom' ? { hours_requested: hours } : {})
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        setShowRequestOptions(false);
        // Show success modal
        const requestData = data.data;
        const startDate = new Date(requestData.start_date).toLocaleDateString();
        const endDate = new Date(requestData.end_date).toLocaleDateString();
        setSuccessMessage(t('vacation_request_confirmed') || `Vacation scheduled: ${startDate} - ${endDate}`);
        setShowSuccessModal(true);
      } else {
        showErr(data.error || 'Failed to submit vacation request');
      }
    } catch (err) {
      console.error('Failed to submit vacation request:', err);
      showErr('Failed to submit vacation request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Add T12:00:00 to avoid timezone shift for date-only strings
    const dateStr = dateString.includes('T') ? dateString : dateString + 'T12:00:00';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatHoursAsWeeks = (hours: number) => {
    const weeks = hours / 40;
    if (weeks >= 1) {
      return `${weeks.toFixed(1)} ${t('vacation_weeks')}`;
    }
    const days = hours / 8;
    if (days >= 1) {
      return `${days.toFixed(1)} ${t('vacation_days')}`;
    }
    return `${hours} ${t('vacation_hours')}`;
  };

  if (loading) {
    return (
      <div className="vacation-page">
        <div className="vacation-loading">
          <div className="spinner"></div>
          <p>{t('loading')}...</p>
        </div>
      </div>
    );
  }

  if (!balance || !balance.hire_date) {
    return (
      <div className="vacation-page">
        <div className="vacation-header">
          <button onClick={() => navigate('/profile')} className="back-button">
            ← {t('back')}
          </button>
          <h1>{t('vacation_time')}</h1>
        </div>
        <div className="vacation-error">
          <p>{t('vacation_no_start_date')}</p>
          <p className="error-hint">{t('contact_admin')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vacation-page">
      <div className="vacation-header">
        <button onClick={() => navigate('/profile')} className="back-button">
          ← {t('back')}
        </button>
        <h1>{t('vacation_time')}</h1>
      </div>

      {error && (
        <div className="vacation-error-banner">
          {error}
        </div>
      )}

      {/* Balance Card */}
      <div className="vacation-balance-card">
        <div className="balance-item">
          <span className="balance-label">{t('vacation_start_date')}</span>
          <span className="balance-value">{formatDate(balance.hire_date)}</span>
        </div>
        <div className="balance-item">
          <span className="balance-label">{t('vacation_total_earned')}</span>
          <span className="balance-value earned">{formatHoursAsWeeks(balance.hours_earned)}</span>
          <span className="balance-hours">({balance.hours_earned} {t('vacation_hours')})</span>
        </div>
        <div className="balance-item">
          <span className="balance-label">{t('vacation_time_used')}</span>
          <span className="balance-value used">{formatHoursAsWeeks(balance.hours_used)}</span>
          <span className="balance-hours">({balance.hours_used} {t('vacation_hours')})</span>
        </div>
        <div className="balance-item highlight">
          <span className="balance-label">{t('vacation_remaining')}</span>
          <span className="balance-value remaining">{formatHoursAsWeeks(balance.hours_remaining)}</span>
          <span className="balance-hours">({balance.hours_remaining} {t('vacation_hours')})</span>
        </div>
        {balance.hours_prorated > 0 && (
          <div className="balance-item prorated">
            <span className="balance-label">{t('vacation_prorated')}</span>
            <span className="balance-value">{formatHoursAsWeeks(balance.hours_prorated)}</span>
          </div>
        )}
      </div>

      {/* Request Buttons */}
      {balance.hours_remaining > 0 && (
        <div className="vacation-request-section">
          {!showRequestOptions ? (
            <button
              onClick={() => setShowRequestOptions(true)}
              className="request-vacation-btn"
              disabled={submitting}
            >
              {t('request_vacation')}
            </button>
          ) : (
            <div className="request-options">
              <h3>{t('select_request_type')}</h3>
              <button
                onClick={() => handleRequestVacation('all_available')}
                className="request-option-btn all"
                disabled={submitting}
              >
                <span className="option-icon">📅</span>
                <span className="option-text">
                  <strong>{t('vacation_request_all')}</strong>
                  <small>{formatHoursAsWeeks(balance.hours_remaining)}</small>
                </span>
              </button>
              {balance.hours_remaining >= 40 && (
                <button
                  onClick={() => handleRequestVacation('one_week')}
                  className="request-option-btn week"
                  disabled={submitting}
                >
                  <span className="option-icon">📆</span>
                  <span className="option-text">
                    <strong>{t('vacation_request_week')}</strong>
                    <small>40 {t('vacation_hours')}</small>
                  </span>
                </button>
              )}
              {balance.hours_remaining >= 40 && (
                <button
                  onClick={() => handleRequestVacation('weekly_until_depleted')}
                  className="request-option-btn weekly"
                  disabled={submitting}
                >
                  <span className="option-icon">🗓️</span>
                  <span className="option-text">
                    <strong>{t('vacation_request_weekly')}</strong>
                    <small>{t('vacation_weekly_desc')}</small>
                  </span>
                </button>
              )}
              <div className="request-option-btn custom" style={{ borderLeft: '4px solid #8b5cf6', cursor: 'default', flexWrap: 'wrap', gap: 12 }}>
                <span className="option-icon">⏱️</span>
                <span className="option-text">
                  <strong>{t('vacation_custom_amount') || 'Custom amount'}</strong>
                  <small>{t('vacation_custom_desc') || 'These hours, this pay period'}</small>
                </span>
                <input
                  type="number"
                  min="0"
                  max={balance.hours_remaining}
                  step="0.5"
                  value={customHours}
                  onChange={(e) => setCustomHours(e.target.value)}
                  disabled={submitting}
                  aria-label="Custom hours"
                  style={{ width: 72, marginLeft: 'auto', padding: '10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 16, textAlign: 'center' }}
                />
                <span style={{ color: '#6b7280', fontSize: 14 }}>{t('vacation_hours') || 'hrs'}</span>
                <button
                  onClick={() => handleRequestVacation('custom')}
                  disabled={submitting}
                  style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#8b5cf6', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                >
                  {t('vacation_request_custom') || 'Request'}
                </button>
              </div>
              <button
                onClick={() => setShowRequestOptions(false)}
                className="cancel-request-btn"
                disabled={submitting}
              >
                {t('cancel')}
              </button>
            </div>
          )}
        </div>
      )}

      {balance.hours_remaining <= 0 && (
        <div className="vacation-no-balance">
          <p>{t('vacation_insufficient_balance')}</p>
        </div>
      )}

      {/* Request History */}
      {requests.length > 0 && (
        <div className="vacation-history">
          <h2>{t('vacation_history')}</h2>
          <div className="request-list">
            {requests.map((item: any, index: number) => (
              item.item_type === 'adjustment' ? (
                <div key={`adj-${item.adjustment_id}`} className="request-item adjustment">
                  <div className="request-dates">
                    {item.adjustment_type === 'manual_deduct' ? t('vacation_deducted') || 'Time Deducted' : t('vacation_added') || 'Time Added'}
                  </div>
                  <div className="request-hours">
                    {item.adjustment_type === 'manual_deduct' ? '-' : '+'}{Math.abs(item.hours_adjusted)} hrs
                  </div>
                  <div className="request-status">
                    {item.reason || (item.adjustment_type === 'manual_deduct' ? 'Used before system' : 'Adjustment')}
                  </div>
                </div>
              ) : (
                <div key={`req-${item.request_id}`} className={`request-item ${item.status}`}>
                  <div className="request-dates">
                    {formatDate(item.start_date)} - {formatDate(item.end_date)}
                  </div>
                  <div className="request-hours">
                    {formatHoursAsWeeks(item.hours_requested)}
                  </div>
                  <div className={`request-status ${item.status}`}>
                    {item.status === 'approved' ? t('approved') : t('cancelled')}
                    {item.is_prorated && <span className="prorated-badge">{t('prorated')}</span>}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="vacation-modal-overlay">
          <div className="vacation-modal">
            <div className="vacation-modal-icon">✓</div>
            <h2>{t('vacation_request_success') || 'Request Submitted!'}</h2>
            <p>{successMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="vacation-modal-btn"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="vacation-modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="vacation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vacation-modal-icon" style={{ color: '#dc2626' }}>⚠️</div>
            <h2>{t('vacation_error_title') || "Couldn't Submit"}</h2>
            <p>{errorModalMsg}</p>
            <button
              onClick={() => setShowErrorModal(false)}
              className="vacation-modal-btn"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
